export type RequestHelperType = {
    parseQueryParams(queryString: string | null | undefined, variable: string): string | null;
    getQueryVariable(variable: string): string | null;
    getQueryVariableFromUrl(url: string, variable: string): string | null;
    decodeMoneySuperMarketParams(encodedValue: string): { appid?: string; campaign?: string; quoteid?: string } | null;
};

export default {
    /**
     * Parses a query string and returns the value for a specific parameter
     * @param queryString The query string to parse (without the leading '?')
     * @param variable The parameter name to search for
     * @returns The parameter value or null if not found
     */
    parseQueryParams(queryString: string | null | undefined, variable: string): string | null {
        if (!queryString || !variable) return null;
        
        try {
            const params = new URLSearchParams(queryString);
            return params.get(variable);
        } catch (error) {
            console.warn('Error parsing query string:', error);
            return null;
        }
    },

    /**
     * Gets a query parameter from the current window location
     */
    getQueryVariable(names: string | string[]): string | null {
        const nameArray = Array.isArray(names) ? names : [names];
        for (const name of nameArray) {
            const value = this.parseQueryParams(window.location.search.substring(1), name);
            if (value) return value;
        }
        return null;
    },

    /**
     * Gets a query parameter from a provided URL string
     */
    getQueryVariableFromUrl(url: string, variable: string): string | null {
        try {
            const urlObject = new URL(url);
            return this.parseQueryParams(urlObject.searchParams.toString(), variable);
        } catch (error) {
            console.warn('Invalid URL provided:', error);
            return null;
        }
    },

    /**
     * Decodes MoneySuperMarket URL-encoded parameters
     * MoneySuperMarket sends parameters as: ?appid=26c56d75-58bf-4a83-85f9-1d6d2711f27f%26campaign%3Dstartlinefinance%26quoteid%3D9a13e45a-c298-4daf-ae14-b832723e46e5
     * This function decodes the URL-encoded value and extracts the individual parameters
     * 
     * @param encodedValue The URL-encoded string containing multiple parameters
     * @returns Object with decoded parameters or null if decoding fails
     */
    decodeMoneySuperMarketParams(encodedValue: string): { appid?: string; campaign?: string; quoteid?: string } | null {
        if (!encodedValue) return null;
        
        try {
            const decodedValue = decodeURIComponent(encodedValue);
            let params: URLSearchParams;

            // Split on & to check the first segment
            const parts = decodedValue.split('&');
            if (parts.length && !parts[0].includes('=')) {
                // First part is the appid value, rest are key=value
                const appidValue = parts[0];
                const rest = parts.slice(1).join('&');
                const reconstructed = `appid=${appidValue}${rest ? '&' + rest : ''}`;
                params = new URLSearchParams(reconstructed);
            } else {
                // Standard query string
                params = new URLSearchParams(decodedValue);
            }

            return {
                appid: params.get('appid') || undefined,
                campaign: params.get('campaign') || undefined,
                quoteid: params.get('quoteid') || undefined
            };
        } catch (error) {
            console.warn('Error decoding MoneySuperMarket parameters:', error);
            return null;
        }
    }
} as const;